import React, { useState, useEffect } from 'react';
import { Radio } from '@mui/material';

import './style.css';
import { HardSoftPreferencePropsType, HardSoftRadioOptions } from './types';

const HardSoftPreference = (props: HardSoftPreferencePropsType) => {

  const {
    value,
    visitClone,
    onRadioButtonChange,
    onInitialLoad,
    careProsList,
    showSelectPreferredCarePros,
    thereAreHomePersonalCareServicesSelected,
    thereAreHomeTherapyServicesSelected,
    thereAreHomeMedicalServicesSelected,
    isReadOnlyMode,
    vref,
    hasError
  } = props;

  const [currentSelection, setCurrentSelection] = useState<HardSoftRadioOptions>(value as HardSoftRadioOptions || 'none');

  // We can only show Hard/Soft preference if a PCP list exists, and a PCP list can only exist if services are selected.
  const personalCareCPs = careProsList?.home_personal_care;
  const therapyCPs = careProsList?.home_therapy;
  const medicalCPs = careProsList?.home_medical;

  const thereArePersonalCareCPs = personalCareCPs && personalCareCPs.length > 0;
  const thereAreTherapyCPs = therapyCPs && therapyCPs.length > 0;
  const thereAreMedicalCPs = medicalCPs && medicalCPs.length > 0;

  const hideHardSoft = !!isReadOnlyMode && !value;

  let pcpListIsAvailable = false;

  if (thereAreHomePersonalCareServicesSelected && thereArePersonalCareCPs) {
    pcpListIsAvailable = true;
  };

  if (thereAreHomeTherapyServicesSelected && thereAreTherapyCPs) {
    pcpListIsAvailable = true;
  };

  if (thereAreHomeMedicalServicesSelected && thereAreMedicalCPs) {
    pcpListIsAvailable = true;
  };

  const componentIsHidden: boolean = !pcpListIsAvailable || !showSelectPreferredCarePros || hideHardSoft;

  const onPreferredCPToggleChange = () => {
    if (showSelectPreferredCarePros === false) {
      setCurrentSelection('none');
      updateVisitOnRadioChange('none');
    };
  };

  const onComponentDidMount = () => {
    if (value) {
      // this visit already exists, so let's prepare the designation object for use in list view
      const updatedVisit = {
        ...visitClone,
        hard_soft_value_designation: getHardSoftPreferenceDesignation(value)
      };

      onInitialLoad(updatedVisit);
    };
  };

  useEffect(() => {
    onPreferredCPToggleChange();
  }, [showSelectPreferredCarePros]);

  useEffect(() => {
    onComponentDidMount();
  }, []);

  const getHardSoftPreferenceDesignation = (value: string) => {
    // We use this object at List View level to know when to hide/show or remove hard/soft pref on change of services/pcp
    if (thereAreHomePersonalCareServicesSelected && thereArePersonalCareCPs) {
      return {
        designation: 'home_personal_care',
        preference: value
      };
    };
    if (thereAreHomeTherapyServicesSelected && thereAreTherapyCPs) {
      return {
        designation: 'home_therapy',
        preference: value
      };
    };
    if (thereAreHomeMedicalServicesSelected && thereAreMedicalCPs) {
      return {
        designation: 'home_medical',
        preference: value
      };
    };
  };

  const updateVisitOnRadioChange = (value: string) => {

    if (value === 'none') {
      // API expects this key not to exist if no radio option was selected.
      delete visitClone.preferred_cps_option;
      delete visitClone.hard_soft_value_designation;
      onRadioButtonChange(visitClone);
    } else {
      const updatedVisit = {
        ...visitClone,
        preferred_cps_option: value,
        hard_soft_value_designation: getHardSoftPreferenceDesignation(value)
      };
      onRadioButtonChange(updatedVisit);
    };
  };

  const onRadioClick = (value: string) => {
    if (value === currentSelection) {
      setCurrentSelection('none');
      updateVisitOnRadioChange('none');
    } else {
      setCurrentSelection(value as HardSoftRadioOptions);
      updateVisitOnRadioChange(value);
    };
  };

  const renderFields = () => {

    if (isReadOnlyMode) {
      return renderReadOnlyField();
    };

    return <div className='hard-soft-row-container'>
      {renderRadioField('Soft Preference', 'Invite and assign other CP(s) if preferred CP(s) is unavailable', 'soft')}
      {renderRadioField('Hard Preference', 'Only invite and assign to preferred CP(s)', 'hard')}
    </div>
  };

  const renderReadOnlyField = () => {
    const valueExists = value && value !== 'none';

    if (!valueExists) {
      return null;
    };

    const isHard = value === 'hard';
    const title = isHard ? 'Hard Preference' : 'Soft Preference';
    const bodyText = isHard ? 'Only invite and assign to preferred CP(s)' : 'Invite and assign other CP(s) if preferred CP(s) is unavailable';

    return <div className='radio-field-row' style={{ marginTop: 16 }}>
      <div className='radio-field-text-container'>
        <div className='radio-field-title'>{title}</div>
        <div className='radio-field-body'>{bodyText}</div>
      </div>
    </div>

  };

  const renderRadioField = (title: string, bodyText: string, value: string) => {
    return <div className='radio-field-row'>
      <Radio
        checked={currentSelection === value}
        value={value}
        disabled={isReadOnlyMode || false}
        onClick={() => onRadioClick(value)}
        size='small'
      />
      <div className='radio-field-text-container'>
        <div className='radio-field-title'>{title}</div>
        <div className='radio-field-body'>{bodyText}</div>
      </div>
    </div>
  };

  const renderError = () => {
    if (isReadOnlyMode || !hasError) {
      return <div ref={vref} />
    };

    return <div className='hard-soft-error-container' ref={vref}>
      <img src='/warning_big@3x.png' alt='warning-sign' className='hard-soft-warning' />
      <div >Please select one option</div>
    </div>
  };

  if (componentIsHidden) {
    return null;
  };

  return (<>
    <div className='hard-soft-container'>
      <div className='hard-soft-title'>Are the preferred CPs a Soft or Hard preference?</div>
      {!isReadOnlyMode && <div className='hard-soft-body'>This can be changed before CP assignment</div>}
      {renderError()}
      {renderFields()}
    </div>
  </>)
}

export default HardSoftPreference;